import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';
import { FaArrowLeft, FaPrint } from 'react-icons/fa';

const getAuthToken = () => localStorage.getItem('authToken');

const FeeStructureView = () => {
  const { feeStructureId } = useParams();
  const navigate = useNavigate();
  const [feeStructure, setFeeStructure] = useState(null);
  const [feeSlabs, setFeeSlabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });

  // Month names for the header
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  // Generate dynamic months based on course period
  const getDynamicMonths = () => {
    if (!feeStructure?.course_start_date || !feeStructure?.course_end_date) {
      return monthNames; // fallback to all months
    }

    const startDate = new Date(feeStructure.course_start_date);
    const endDate = new Date(feeStructure.course_end_date);
    const months = [];

    let currentDate = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      1,
    );

    while (currentDate <= endDate) {
      months.push(monthNames[currentDate.getMonth()]);
      currentDate.setMonth(currentDate.getMonth() + 1);
    }

    return months;
  };

  useEffect(() => {
    fetchFeeStructureDetails();
  }, [feeStructureId]);

  const fetchFeeStructureDetails = async () => {
    const token = getAuthToken();
    if (!token) {
      showAlert('Unauthorized! Please log in.', 'danger');
      return;
    }

    setLoading(true);
    try {
      // Fetch fee structure with full details (slabs and installments)
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}${appConstants.FEE_STRUCTURE.GET}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { id: feeStructureId },
        },
      );

      if (response.data.status === 'success') {
        setFeeStructure(response.data.data);

        // Fetch full fee structure with slabs and installments
        const fullResponse = await axios.get(
          `${appConstants.API_ROOT_URL}${appConstants.FEE_STRUCTURE.GET_FULL}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { fee_structure_id: feeStructureId },
          },
        );

        if (fullResponse.data.status === 'success') {
          setFeeSlabs(fullResponse.data.data || []);
        } else {
          showAlert('Failed to fetch fee slabs details', 'danger');
        }
      } else {
        showAlert('Failed to fetch fee structure details', 'danger');
      }
    } catch (error) {
      console.error('Error fetching fee structure details:', error);
      showAlert('Error loading fee structure details', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, variant) => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: '', variant: '' }), 5000);
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDateRange = (fromDate, toDate) => {
    const formatDate = (dateStr) => {
      const date = new Date(dateStr);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };
    return `${formatDate(fromDate)} To ${formatDate(toDate)}`;
  };

  // Get installments organized by month for display within slab period
  const getInstallmentsByMonth = (slab) => {
    const installments = slab.installments || [];
    const dynamicMonths = getDynamicMonths();
    const monthlyInstallments = {};

    // Initialize all dynamic months with empty values
    dynamicMonths.forEach((month) => {
      monthlyInstallments[month] = '';
    });

    // Get slab period start and end dates
    const slabStartDate = new Date(slab.from_date);
    const slabEndDate = new Date(slab.to_date);

    // Calculate months within the slab period
    const slabMonths = [];
    let currentDate = new Date(
      slabStartDate.getFullYear(),
      slabStartDate.getMonth(),
      1,
    );

    // Handle invalid date ranges (when to_date is before from_date)
    if (slabEndDate < slabStartDate) {
      // Use all dynamic months as fallback when slab dates are invalid
      slabMonths.push(...dynamicMonths);
    } else {
      // Normal case: calculate months within the slab period
      while (currentDate <= slabEndDate) {
        const monthName = monthNames[currentDate.getMonth()];
        if (dynamicMonths.includes(monthName)) {
          slabMonths.push(monthName);
        }
        currentDate.setMonth(currentDate.getMonth() + 1);
      }
    }

    // If no months found, use all dynamic months as fallback
    if (slabMonths.length === 0) {
      slabMonths.push(...dynamicMonths);
    }

    // Distribute installments across months within the slab period
    installments.forEach((installment) => {
      if (slabMonths.length > 0) {
        // Calculate target month based on due_after_days from slab start
        const monthOffset = Math.floor(installment.due_after_days / 30);
        const targetMonthIndex = Math.min(monthOffset, slabMonths.length - 1);
        const targetMonth = slabMonths[targetMonthIndex];

        if (targetMonth && dynamicMonths.includes(targetMonth)) {
          if (monthlyInstallments[targetMonth] === '') {
            monthlyInstallments[targetMonth] = parseFloat(
              installment.amount || 0,
            );
          } else {
            monthlyInstallments[targetMonth] += parseFloat(
              installment.amount || 0,
            );
          }
        }
      }
    });

    return monthlyInstallments;
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-4">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Layout>
    );
  }

  if (!feeStructure) {
    return (
      <Layout>
        <div className="text-center py-4">
          <Alert variant="danger">Fee structure not found</Alert>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3 no-print">
          <Button
            variant="outline-secondary"
            onClick={() => navigate('/fees-structure')}
          >
            <FaArrowLeft className="me-2" />
            Back to Fee Structures
          </Button>
          <Button variant="primary" onClick={handlePrint}>
            <FaPrint className="me-2" />
            Print
          </Button>
        </div>

        {/* Fee Structure Header */}
        <div className="text-center mb-4">
          <h3
            style={{
              backgroundColor: '#c8e6c9',
              padding: '10px',
              margin: '0',
              fontWeight: 'bold',
            }}
          >
            {feeStructure.name} (
            {formatDateRange(
              feeStructure.course_start_date,
              feeStructure.course_end_date,
            )}
            )
          </h3>
        </div>

        {/* Fee Structure Table */}
        <div className="table-responsive">
          <table
            className="table table-bordered"
            style={{ fontSize: '12px', border: '2px solid #000' }}
          >
            <thead style={{ backgroundColor: '#ffeb3b' }}>
              <tr>
                <th
                  style={{
                    backgroundColor: '#ffeb3b',
                    fontWeight: 'bold',
                    border: '1px solid #000',
                    textAlign: 'center',
                    verticalAlign: 'middle',
                  }}
                >
                  Admission
                  <br />
                  Date
                </th>
                <th
                  style={{
                    backgroundColor: '#ffeb3b',
                    fontWeight: 'bold',
                    border: '1px solid #000',
                    textAlign: 'center',
                    verticalAlign: 'middle',
                  }}
                >
                  Add.
                  <br />
                  Fee
                </th>
                <th
                  style={{
                    backgroundColor: '#ffeb3b',
                    fontWeight: 'bold',
                    border: '1px solid #000',
                    textAlign: 'center',
                    verticalAlign: 'middle',
                  }}
                >
                  Tuition
                  <br />
                  Fee Total
                </th>
                <th
                  style={{
                    backgroundColor: '#ffeb3b',
                    fontWeight: 'bold',
                    border: '1px solid #000',
                    textAlign: 'center',
                    verticalAlign: 'middle',
                  }}
                >
                  Less
                </th>
                <th
                  style={{
                    backgroundColor: '#ffeb3b',
                    fontWeight: 'bold',
                    border: '1px solid #000',
                    textAlign: 'center',
                    verticalAlign: 'middle',
                  }}
                >
                  Tuition Fee
                  <br />
                  Effective
                </th>
                {getDynamicMonths().map((month) => (
                  <th
                    key={month}
                    style={{
                      backgroundColor: '#ffeb3b',
                      fontWeight: 'bold',
                      border: '1px solid #000',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                    }}
                  >
                    {month}.
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {feeSlabs.map((slab, index) => {
                const monthlyInstallments = getInstallmentsByMonth(slab);
                const isEvenRow = index % 2 === 0;
                const rowStyle = {
                  backgroundColor: isEvenRow ? '#f5f5f5' : '#e0e0e0',
                };

                return (
                  <tr key={slab.id} style={rowStyle}>
                    <td
                      style={{
                        backgroundColor: '#c8e6c9',
                        fontWeight: 'bold',
                        border: '1px solid #000',
                        padding: '8px',
                        textAlign: 'center',
                      }}
                    >
                      {formatDateRange(slab.from_date, slab.to_date)}
                    </td>
                    <td
                      style={{
                        border: '1px solid #000',
                        padding: '8px',
                        textAlign: 'center',
                      }}
                    >
                      {Math.round(
                        parseFloat(feeStructure.admission_fee || 0),
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: '1px solid #000',
                        padding: '8px',
                        textAlign: 'center',
                      }}
                    >
                      {Math.round(
                        parseFloat(feeStructure.tuition_fee || 0),
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        backgroundColor: '#ffcc80',
                        border: '1px solid #000',
                        padding: '8px',
                        textAlign: 'center',
                      }}
                    >
                      {Math.round(
                        parseFloat(slab.tuition_discount || 0),
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        backgroundColor: '#ffcc80',
                        border: '1px solid #000',
                        padding: '8px',
                        textAlign: 'center',
                      }}
                    >
                      {Math.round(
                        parseFloat(slab.effective_tuition_fee || 0),
                      ).toLocaleString()}
                    </td>
                    {getDynamicMonths().map((month) => (
                      <td
                        key={month}
                        style={{
                          border: '1px solid #000',
                          padding: '8px',
                          textAlign: 'center',
                          minWidth: '60px',
                        }}
                      >
                        {monthlyInstallments[month] &&
                        monthlyInstallments[month] !== ''
                          ? Math.round(
                              monthlyInstallments[month],
                            ).toLocaleString()
                          : ''}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Floating Alert (bottom left) */}
        {alert.show && (
          <div
            style={{
              position: 'fixed',
              left: '18px',
              bottom: '18px',
              zIndex: 9999,
              minWidth: '180px',
              maxWidth: '320px',
              pointerEvents: 'auto',
              boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
            }}
          >
            <div
              style={{
                position: 'relative',
                borderRadius: '6px',
                background:
                  alert.variant === 'success'
                    ? '#e6f7e6'
                    : alert.variant === 'danger'
                      ? '#ffeaea'
                      : '#fff8e1',
                color: '#5c4200',
                marginBottom: 0,
                fontWeight: 500,
                fontSize: '0.92rem',
                padding: '6px 32px 6px 12px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                minHeight: '32px',
                display: 'flex',
                alignItems: 'center',
                maxWidth: '320px',
              }}
            >
              <span style={{ flex: 1 }}>{alert.message}</span>
              <button
                onClick={() =>
                  setAlert({ show: false, message: '', variant: '' })
                }
                style={{
                  position: 'absolute',
                  top: '50%',
                  right: '8px',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#5c4200',
                  fontWeight: 700,
                  fontSize: '1.1em',
                  cursor: 'pointer',
                  padding: 0,
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @media print {
          .no-print {
            display: none !important;
          }

          body {
            font-size: 12px;
          }

          table {
            width: 100% !important;
            border-collapse: collapse !important;
          }

          th,
          td {
            border: 1px solid #000 !important;
            padding: 4px !important;
            font-size: 10px !important;
          }
        }
      `}</style>
    </Layout>
  );
};

export default FeeStructureView;
